import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Matriculas";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarMatriculas = ({ id }) => {
  const [matriculas, setMatriculas] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO MATRÍCULAS
  // ==============================
  const getModuloMatriculas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloMatriculas = useMemo(
    () => getModuloMatriculas(),
    []
  );

  const puedeCrear =
    moduloMatriculas?.puedeCrear === true ||
    moduloMatriculas?.crear === true;

  const puedeActualizar =
    moduloMatriculas?.puedeActualizar === true ||
    moduloMatriculas?.actualizar === true;

  const puedeEliminar =
    moduloMatriculas?.puedeEliminar === true ||
    moduloMatriculas?.eliminar === true;

  // ==============================
  // CELDAS CLICKEABLES
  // ==============================
  const celdaClickeable = (
    contenido,
    matriculaId,
    title = "Abrir matrícula"
  ) => (
    <button
      type="button"
      className="btn p-0 w-100 h-100 text-start border-0 bg-transparent"
      style={{
        cursor: "pointer",
        lineHeight: 1.2,
      }}
      onClick={() =>
        navigate(`/matricula/listarmatriculados/${matriculaId}`)
      }
      title={title}
    >
      {contenido}
    </button>
  );

  // ==============================
  // ELIMINAR MATRÍCULA
  // ==============================
  const deleteMatricula = useCallback(
    async (matriculaId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar matrículas.",
          "warning"
        );
        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        const { data } = await axios.delete(
          `/api/matriculas/${matriculaId}`,
          authConfig()
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Matrícula eliminada",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar matrícula:", error);

        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "No se pudo eliminar la matrícula.",
          "error"
        );
      }
    },
    [puedeEliminar]
  );

  // ==============================
  // ACCIONES SELECT
  // ==============================
  const handleAccion = useCallback(
    async (accion, matricula) => {
      if (!accion) return;

      switch (accion) {

        case "matriculados":
          navigate(
            `/matricula/listarmatriculados/${matricula._id}`
          );
          break;

        case "seguimientos":
          navigate(
            `/etapaproductiva/seguimiento/registromasivo/${matricula._id}`
          );
          break;

        case "bitacoras":
          navigate(
            `/etapaproductiva/bitacoras/registromasivo/${matricula._id}`
          );
          break;

        case "etapa":
          navigate(
            `/etapaproductiva/registromasivo/${matricula._id}`
          );
          break;

        case "asistencia":
          navigate(
            `/actividad/asistencia/${matricula._id}`
          );
          break;

        case "editar":

          if (!puedeActualizar) {
            Swal.fire(
              "Sin permisos",
              "No tiene permisos para actualizar matrículas.",
              "warning"
            );

            return;
          }

          navigate(`/matricula/editar/${matricula._id}`);

          break;

        case "eliminar":

          if (!puedeEliminar) {
            Swal.fire(
              "Sin permisos",
              "No tiene permisos para eliminar matrículas.",
              "warning"
            );

            return;
          }

          await deleteMatricula(matricula._id);

          await getData();

          break;

        default:
          break;
      }
    },
    [
      navigate,
      puedeActualizar,
      puedeEliminar,
      deleteMatricula,
    ]
  );

  // ==============================
  // OBTENER DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        "/api/matriculas/listall/",
        authConfig()
      );

      const lista = data?.matriculas || [];

      const matriculasConFunciones = lista.map(
        (matricula) => {
          const nombreProgramaRaw =
            matricula?.grupoObjectId?.programaObjectId
              ?.nombrePrograma || "Sin programa";

          const programaCell = (
            <div
              className="text-truncate"
              style={{ maxWidth: 380 }}
              title={nombreProgramaRaw}
            >
              {nombreProgramaRaw}
            </div>
          );

          return {
            ...matricula,

            anioLectivo: celdaClickeable(
              matricula?.anioLectivo ?? "—",
              matricula?._id
            ),

            fichaGrupo: celdaClickeable(
              matricula?.grupoObjectId?.fichaGrupo ||
              "Sin asignar",
              matricula?._id,
              "Ver matriculados"
            ),

            nombrePrograma: celdaClickeable(
              programaCell,
              matricula?._id
            ),

            cantCuposGrupo: celdaClickeable(
              matricula?.grupoObjectId?.cantCuposGrupo ??
              "N/A",
              matricula?._id
            ),

            detallesCount: celdaClickeable(
              matricula?.detallesCount ?? 0,
              matricula?._id
            ),

            cantidadDesertados: celdaClickeable(
              matricula?.cantidadDesertados ?? 0,
              matricula?._id
            ),

            cantidadTraslados: celdaClickeable(
              matricula?.cantidadTraslados ?? 0,
              matricula?._id
            ),

            funciones: (
              <div className="d-flex justify-content-end">
                <div
                  className="input-group input-group-sm"
                  style={{ width: 250 }}
                >

                  <span className="input-group-text bg-white">
                    <i className="fa-solid fa-gear"></i>
                  </span>

                  <select
                    className="form-select"
                    defaultValue=""
                    onChange={(e) => {
                      const accion = e.target.value;

                      e.target.value = "";

                      handleAccion(
                        accion,
                        matricula
                      );
                    }}
                  >

                    <option value="" disabled>
                      Acciones de matrícula…
                    </option>

                    <optgroup label="Gestión Académica">

                      <option value="matriculados">
                        👥 Ver Matriculados
                      </option>

                      <option value="seguimientos">
                        📝 Seguimientos Masivos
                      </option>

                      <option value="bitacoras">
                        📚 Bitácoras Masivas
                      </option>

                      <option value="etapa">
                        💼 Etapa Productiva Masiva
                      </option>

                      <option value="asistencia">
                        ✅ Actividad Asistencia
                      </option>

                    </optgroup>

                    <optgroup label="Administración">

                      {puedeActualizar && (
                        <option value="editar">
                          ✏️ Editar Matrícula
                        </option>
                      )}

                      {puedeEliminar && (
                        <option value="eliminar">
                          🗑️ Eliminar Matrícula
                        </option>
                      )}

                    </optgroup>

                  </select>
                </div>
              </div>
            ),
          };
        }
      );

      setMatriculas(matriculasConFunciones);
    } catch (error) {
      console.error(
        "Error al cargar matrículas:",
        error
      );

      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "No se pudo cargar la lista de matrículas.",
        "error"
      );

      setMatriculas([]);
    } finally {
      setLoading(false);
    }
  }, [
    handleAccion,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  // ==============================
  // COLUMNAS
  // ==============================
  const columns = useMemo(
    () => [
      { Header: "Año", accessor: "anioLectivo" },
      { Header: "Grupo", accessor: "fichaGrupo" },
      { Header: "Programa", accessor: "nombrePrograma" },
      { Header: "Solicitados", accessor: "cantCuposGrupo" },
      { Header: "Matriculados", accessor: "detallesCount" },
      { Header: "Deserciones", accessor: "cantidadDesertados" },
      { Header: "Traslados", accessor: "cantidadTraslados" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="fw-semibold mb-1">
                  Matrículas
                </h5>

                <small className="text-secondary">
                  Gestión de matrículas, cupos y acciones masivas.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark flex-fill"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  title="Volver al inicio"
                  disabled={loading}
                >
                   <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary flex-fill"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark flex-fill"
                    type="button"
                    onClick={() => navigate("/matricula/registrar")}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border"
                  role="status"
                />
                <div className="mt-3">
                  Cargando matrículas…
                </div>
              </div>
            ) : matriculas.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={matriculas}
                />

              </div>
            ) : (
              <div className="text-center py-5 text-secondary">
                <div className="display-6 mb-2">🧾</div>

                <div className="fw-semibold">
                  No hay matrículas registradas
                </div>

                <div className="small mb-3">
                  Crea la primera matrícula para iniciar la gestión.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate("/matricula/registrar")}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear matrícula
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarMatriculas;